import {Input} from 'antd';

import {useLocale} from '../../provider/locale/locale-hook';
import {LangKeyType} from '../../provider/locale/translation/type';
import {classNames} from '../../util/css';
import {debounce} from '../../util/function';

import * as styles from './list-search-input.scss';

const SEARCH_DEBOUNCE_WAIT_MS = 300;

type PropsType = {
    onSearch: (query: string) => void;
    isLoading?: boolean;
    placeholderLangKey?: LangKeyType;
    className?: string;
};

/**
 * Reusable search-by-name input for server-paginated settings lists.
 *
 * Debounces typing and emits the trimmed query via `onSearch`; the parent feeds
 * that value into its list request and resets pagination to the first page.
 */
export function ListSearchInput(props: PropsType): JSX.Element {
    const {onSearch, isLoading, placeholderLangKey, className} = props;
    const {getLocalizedString} = useLocale();

    const debouncedSearch = debounce<[string]>((value) => onSearch(value.trim()), SEARCH_DEBOUNCE_WAIT_MS);

    return (
        <Input.Search
            allowClear
            className={classNames(styles.ListSearchInput, className)}
            // Not disabled while loading: a disabled-then-re-enabled input loses focus,
            // which would interrupt typing. `loading` shows the spinner without blocking input.
            loading={isLoading}
            onChange={(event) => debouncedSearch(event.target.value)}
            placeholder={getLocalizedString(placeholderLangKey ?? 'TABLE__SEARCH__PLACEHOLDER')}
        />
    );
}
